import * as Sentry from '@sentry/react';
import axios, { AxiosError, AxiosInstance, InternalAxiosRequestConfig } from 'axios';

import { V2_READ_ONLY_MESSAGE } from '@/configs/v2Write';

interface CreateApiInstanceOptions {
  onUnauthorized?: () => void;
  /** Только чтение: запросы на запись отклоняются, не уходя на бэк */
  readOnly?: boolean;
}

/** Запрос на запись, отклонённый в режиме только чтения; в Sentry не уходит — это не сбой */
export class ReadOnlyRequestError extends Error {
  constructor() {
    super(V2_READ_ONLY_MESSAGE);
    this.name = 'ReadOnlyRequestError';
  }
}

// Чтение — GET, а ещё POST поиска и подсказок (`tasks:search`, `users:suggest`): тело у них — фильтры
const isReadRequest = (config: InternalAxiosRequestConfig): boolean => {
  const method = (config.method ?? 'get').toLowerCase();
  const path = (config.url ?? '').split('?')[0];

  return method === 'get' || /:(search|suggest)$/.test(path);
};

export function createApiInstance(tag: string, options?: CreateApiInstanceOptions): AxiosInstance {
  const instance = axios.create({
    baseURL: import.meta.env.VITE_API_URL,
    headers: {
      'Content-Type': 'application/json',
    },
    withCredentials: true,
  });

  if (options?.readOnly) {
    instance.interceptors.request.use((config) => {
      if (isReadRequest(config)) return config;

      throw new ReadOnlyRequestError();
    });
  }

  instance.interceptors.response.use(
    (response) => response,
    (error: AxiosError) => {
      if (error instanceof ReadOnlyRequestError) return Promise.reject(error);

      if (error.response?.status === 401) {
        options?.onUnauthorized?.();
      } else {
        Sentry.captureException(error, {
          tags: {
            api: tag,
            status: error.response?.status,
          },
          extra: {
            url: error.config?.url,
            method: error.config?.method,
            responseData: error.response?.data,
          },
        });
      }

      return Promise.reject(error);
    },
  );

  return instance;
}
